import http, { jsonToImmutableError, middleware as mw } from '@gonfalon/legacy-http-client/httpUtils';

import type { Environment } from '#utils/environments/records/Environment';
import { createEnvironment } from '#utils/environments/records/Environment';
import { createJsonPatch } from '#utils/patchUtils';
import {
  createNormalizedProjectEntity,
  processPaginatedProjectCollectionRep,
  processProjectCollectionRep,
  processProjectEnvironmentsCollection,
} from '#utils/projects/processCollections';
import type { NormalizedProjectCollectionResponse, NormalizedProjectResponse } from '#utils/projects/projectTypes';
import type { EnvironmentsFilters, ProjectFilters } from '#utils/projects/records/Filters';
import type { NewProject } from '#utils/projects/records/NewProject';
import type { Project } from '#utils/projects/records/Project';

export function getAllProjectsV3(projectFilters?: ProjectFilters): Promise<NormalizedProjectCollectionResponse> {
  const headers = { headers: { 'LD-API-Version': '20220603' } };
  const url = projectFilters?.toBackendQueryString()
    ? `/api/v2/projects${projectFilters?.toBackendQueryString()}&expand=environments`
    : '/api/v2/projects?expand=environments';
  return http.get(url, headers).then(mw.json).then(processPaginatedProjectCollectionRep).catch(jsonToImmutableError);
}

export function getAllProjects(): Promise<NormalizedProjectCollectionResponse> {
  const headers = { headers: { 'LD-API-Version': '20210729' } };

  return http
    .get('/api/v2/projects', headers)
    .then(mw.json)
    .then(processProjectCollectionRep)
    .catch(jsonToImmutableError);
}

export function fetchProjectEnvironments(projectKey: string, filters: EnvironmentsFilters) {
  return http
    .get(`/api/v2/projects/${projectKey}/environments${filters.toBackendQueryString()}`)
    .then(mw.json)
    .then(processProjectEnvironmentsCollection)
    .catch(jsonToImmutableError);
}

export function fetchProjectEnvironment(projectKey: string, environmentKey: string): Promise<Environment> {
  return http
    .get(`/api/v2/projects/${projectKey}/environments/${environmentKey}`)
    .then(mw.json)
    .then(createEnvironment)
    .catch(jsonToImmutableError);
}

export function createProject(project: NewProject): Promise<NormalizedProjectResponse> {
  return http
    .post('/api/v2/projects', { body: project })
    .then(mw.json)
    .then(createNormalizedProjectEntity)
    .catch(jsonToImmutableError);
}

export function updateProject(oldProject: Project, newProject: Project): Promise<NormalizedProjectResponse> {
  const patch = createJsonPatch(oldProject, newProject, { propFilter: false });
  return http
    .patch(oldProject.selfLink(), { body: patch })
    .then(mw.json)
    .then(createNormalizedProjectEntity)
    .catch(jsonToImmutableError);
}

export function getProject(projectKey: string) {
  return http
    .get(`/api/v2/projects/${projectKey}`, { headers: { 'LD-API-Version': '20210729' } })
    .then((res) => res.json().then(createNormalizedProjectEntity))
    .catch(jsonToImmutableError);
}

export function deleteProject(project: Project): Promise<void> {
  return http.delete(project.selfLink()).catch(jsonToImmutableError);
}
